class DynamicThresholdObserver {
	constructor(callback, options = {}) {
		this.callback = callback;
		this.options = options;

		// Threshold per direzioni diverse
		this.thresholds = {
			down: options.thresholdDown || 0.1, // Scroll verso il basso
			up: options.thresholdUp || 0.05, // Scroll verso l'alto
		};

		// Tracking dello scroll
		this.lastScrollY = window.scrollY;
		this.scrollDirection = "down";
		this.isScrolling = false;

		// Elementi osservati e loro stati
		this.observedElements = new Map();

		// Crea due observer con threshold diversi
		this.downObserver = this.createObserver(this.thresholds.down);
		this.upObserver = this.createObserver(this.thresholds.up);

		// Setup scroll tracking
		this.setupScrollTracking();

		// console.log("DynamicThresholdObserver initialized:", this.thresholds);
	}

	setupScrollTracking() {
		let scrollTimeout;

		window.addEventListener(
			"scroll",
			() => {
				const currentScrollY = window.scrollY;

				// Determina direzione dello scroll
				if (currentScrollY > this.lastScrollY) {
					this.scrollDirection = "down";
				} else if (currentScrollY < this.lastScrollY) {
					this.scrollDirection = "up";
				}

				this.lastScrollY = currentScrollY;
				this.isScrolling = true;

				// Reset dello stato scrolling dopo un breve periodo
				clearTimeout(scrollTimeout);
				scrollTimeout = setTimeout(() => {
					this.isScrolling = false;
				}, 150);
			},
			{ passive: true }
		);
	}

	createObserver(threshold) {
		return new IntersectionObserver(
			(entries) => {
				this.handleIntersection(entries, threshold);
			},
			{
				...this.options,
				threshold: threshold,
			}
		);
	}

	handleIntersection(entries, usedThreshold) {
		entries.forEach((entry) => {
			const elementId = this.getElementId(entry.target);
			const elementState = this.observedElements.get(elementId);

			if (!elementState) return;

			// Determina quale threshold dovrebbe essere usato
			const expectedThreshold = this.scrollDirection === "down" ? this.thresholds.down : this.thresholds.up;

			// Processa solo se il threshold corrisponde alla direzione dello scroll
			if (Math.abs(usedThreshold - expectedThreshold) < 0.001) {
				// Aggiorna lo stato dell'elemento
				const wasIntersecting = elementState.isIntersecting;
				elementState.isIntersecting = entry.isIntersecting;
				elementState.intersectionRatio = entry.intersectionRatio;
				elementState.scrollDirection = this.scrollDirection;

				// Chiama il callback solo se lo stato è effettivamente cambiato
				if (wasIntersecting !== entry.isIntersecting) {
					//console.log(`Element ${elementId}: ${entry.isIntersecting ? 'entering' : 'leaving'} (scroll: ${this.scrollDirection}, threshold: ${usedThreshold})`);

					// Passa l'entry originale e i metadati separatamente
					this.callback([entry], {
						scrollDirection: this.scrollDirection,
						usedThreshold: usedThreshold,
					});
				}
			}
		});
	}

	observe(element) {
		const elementId = this.getElementId(element);

		// Salva lo stato iniziale dell'elemento
		this.observedElements.set(elementId, {
			element: element,
			isIntersecting: false,
			intersectionRatio: 0,
			scrollDirection: "down",
		});

		// Osserva con entrambi gli observer
		this.downObserver.observe(element);
		this.upObserver.observe(element);

		//console.log(`Started observing element: ${elementId}`);
	}

	unobserve(element) {
		const elementId = this.getElementId(element);

		this.observedElements.delete(elementId);
		this.downObserver.unobserve(element);
		this.upObserver.unobserve(element);

		//console.log(`Stopped observing element: ${elementId}`);
	}

	disconnect() {
		this.observedElements.clear();
		this.downObserver.disconnect();
		this.upObserver.disconnect();

		//console.log('DynamicThresholdObserver disconnected');
	}

	getElementId(element) {
		return element.id || element.className.split(" ")[0] || element.tagName + "_" + Array.from(element.parentNode.children).indexOf(element);
	}
}

document.addEventListener("DOMContentLoaded", function () {
	const logo = document.querySelector(".site-logo");
	const heroSwiper = document.querySelector(".hero-swiper");
	const heroSlideshow = document.querySelector(".hero-slideshow");

	let isHeroVisible = false;
	let currentSwiperInstance = null;

	class LogoManager {
		constructor() {
			this.logo = logo;
			this.setupHeroVisibilityObserver();
			this.setupSectionsObserver();
			this.setupSwiperHandling();
		}

		// Controllo iniziale al caricamento della pagina
		performInitialCheck() {
			//console.log('Performing initial logo check...');

			// 1. Controlla se l'hero è visibile
			const heroElement = heroSwiper || heroSlideshow;

			if (heroElement) {
				const heroRect = heroElement.getBoundingClientRect();
				const headerHeight = this.getHeaderHeight();

				// Se l'hero è visibile sotto l'header
				if (heroRect.top < window.innerHeight && heroRect.bottom > headerHeight) {
					isHeroVisible = true;
					//console.log('Initial check: Hero is visible');

					// Se Swiper è già inizializzato, controlla la slide attiva
					if (currentSwiperInstance) {
						this.updateLogoFromSwiper(currentSwiperInstance);
						return; // Exit early, hero ha priorità
					}

					// Fallback: controlla se l'hero stesso ha bg-dark
					if (heroElement.classList.contains("bg-dark")) {
						this.updateLogoColor(true);
						return;
					}
				}
			}

			// 2. Se l'hero non è visibile o non gestisce il logo, controlla le sezioni
			this.checkCurrentSections();
		}

		checkCurrentSections() {
			const headerHeight = this.getHeaderHeight();
			const sections = document.querySelectorAll(".bg-dark:not(.swiper-slide)");

			//console.log('Manually checking current sections...');

			for (const section of sections) {
				const rect = section.getBoundingClientRect();
				const isIntersecting = rect.top < window.innerHeight && rect.bottom > headerHeight;

				if (isIntersecting) {
					//console.log('Manual check: Found intersecting bg-dark section');
					this.updateLogoColor(true);
					return;
				}
			}

			// Se nessuna sezione dark è visibile, imposta logo nero
			//console.log('Manual check: No dark sections visible, setting logo to black');
			this.updateLogoColor(false);
		}

		getHeaderHeight() {
			const header = document.querySelector("header") || document.querySelector(".header") || document.querySelector(".site-header");
			return header ? header.offsetHeight : 100; // Fallback di 100px
		}

		setupHeroVisibilityObserver() {
			const heroElement = heroSwiper || heroSlideshow;
			const headerHeight = this.getHeaderHeight();

			if (!heroElement) return;

			const heroObserver = new DynamicThresholdObserver(
				(entries, metadata) => {
					entries.forEach((entry) => {
						isHeroVisible = entry.isIntersecting;
						//console.log(`Hero visibility changed: ${isHeroVisible} (scroll: ${metadata.scrollDirection}, threshold: ${metadata.usedThreshold})`);

						if (isHeroVisible && currentSwiperInstance) {
							this.updateLogoFromSwiper(currentSwiperInstance);
						}
					});
				},
				{
					thresholdDown: 0.01, // Threshold basso quando si scrolla verso il basso
					thresholdUp: 0.01, // Threshold più alto quando si scrolla verso l'alto
					rootMargin: `-${headerHeight}px 0px -${headerHeight}px 0px`,
				}
			);

			heroObserver.observe(heroElement);
		}

		setupSectionsObserver() {
			const sections = document.querySelectorAll(".bg-dark:not(.swiper-slide), section:not(.hero-slideshow)");
			const headerHeight = this.getHeaderHeight();
			const sectionsObserver = new DynamicThresholdObserver(
				(entries, metadata) => {
					//console.log('isHeroVisible: ' + isHeroVisible);

					if (!isHeroVisible) {
						entries.forEach((entry) => {
							//console.log('entry: ' + entry.target);

							if (entry.isIntersecting) {
								//console.log(`Section intersecting (hero not visible, scroll: ${metadata.scrollDirection}, threshold: ${metadata.usedThreshold})`);
								this.updateLogoColor(entry.target.classList.contains("bg-dark"));
							}
						});
					}
				},
				{
					thresholdDown: 0.01, // Threshold basso quando si scrolla verso il basso
					thresholdUp: 0.01, // Threshold più alto quando si scrolla verso l'alto
					rootMargin: `0px 0px -${window.innerHeight - headerHeight}px 0px`,
				}
			);

			sections.forEach((section) => sectionsObserver.observe(section));
		}

		setupSwiperHandling() {
			if (heroSwiper) {
				const checkExistingSwiper = () => {
					const swiper = heroSwiper.swiper;

					if (swiper) {
						currentSwiperInstance = swiper;
						this.bindSwiperEvents(swiper);

						// Controllo iniziale se l'hero è già visibile
						if (isHeroVisible) {
							this.updateLogoFromSwiper(swiper);
						}
					} else {
						setTimeout(checkExistingSwiper, 100);
					}
				};

				checkExistingSwiper();
			}

			if (heroSlideshow) {
				this.setupMutationObserver();
			}
		}

		bindSwiperEvents(swiper) {
			swiper.on("slideChange", () => {
				if (isHeroVisible) {
					//console.log('Swiper slide changed (hero visible)');
					this.updateLogoFromSwiper(swiper);
				}
			});
		}

		setupMutationObserver() {
			const swiperContainer = heroSlideshow.querySelector(".swiper-wrapper");

			if (!swiperContainer) return;

			const mutationObserver = new MutationObserver(() => {
				if (isHeroVisible) {
					const activeSlide = swiperContainer.querySelector(".swiper-slide-active");

					if (activeSlide && activeSlide.checkVisibility()) {
						this.updateLogoColor(activeSlide.classList.contains("bg-dark"));
					}
				}
			});

			mutationObserver.observe(swiperContainer, {
				attributes: true,
				subtree: true,
				attributeFilter: ["class"],
			});
		}

		updateLogoFromSwiper(swiperInstance) {
			const activeSlide = swiperInstance.slides[swiperInstance.activeIndex];

			if (activeSlide) {
				const isDark = activeSlide.classList.contains("bg-dark");
				this.updateLogoColor(isDark);
			}
		}

		updateLogoColor(isDark) {
			if (!this.logo) return;

			if (isDark) {
				this.logo.style.filter = "brightness(0) invert(1)";
				//console.log('Logo set to WHITE');
			} else {
				this.logo.style.filter = "none";
				//console.log('Logo set to BLACK');
			}
		}
	}

	// Inizializza il gestore del logo
	const logoManager = new LogoManager();

	// Controllo iniziale al caricamento della pagina
	setTimeout(() => {
		logoManager.performInitialCheck();
	}, 100); // Piccolo delay per assicurarsi che tutto sia renderizzato
});
